import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@icetus/ui';
import type { ProvisioningView } from '@icetus/contracts';
import { ApiError, fetchProvisioning, retryProvisioning } from '../api';

/**
 * 开通进度。
 *
 * 这块界面只在"账号还没有任何租户"时出现 —— 也就是注册完之后、开通跑完之前
 * 那几秒，或者开通失败之后的任何时候。它要回答三个问题：
 *   1) 现在到哪一步了（不是转圈）；
 *   2) 还要不要等（成功 / 失败 / 正在跑）；
 *   3) 失败了怎么办（重试按钮，而不是"请联系客服"）。
 *
 * 轮询而不是长连接：开通全程不到一秒，SSE/WebSocket 的连接建立成本比
 * 整个流程还高，而这条路径上唯一会反复请求的就是这个页面。
 */

const POLL_MS = 1200;
/** 超过这个次数就停：一直转的界面比"还没好"更让人焦虑，那时候该给重试按钮 */
const MAX_POLLS = 30;

export function ProvisioningPanel({ onReady }: { onReady: () => void }) {
  const [view, setView] = useState<ProvisioningView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [polls, setPolls] = useState(0);
  const cancelled = useRef(false);

  const load = useCallback(async (): Promise<ProvisioningView | null> => {
    try {
      const next = await fetchProvisioning();
      if (!cancelled.current) {
        setView(next);
        setError(null);
      }
      return next;
    } catch (err) {
      if (!cancelled.current) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
      return null;
    }
  }, []);

  // 轮询：只在"还没结论"时继续。一旦 succeeded / failed 就停下来 ——
  // 对一个已经失败的任务每 1.2 秒再问一次，除了费电没有任何作用
  useEffect(() => {
    cancelled.current = false;
    let timer: number | undefined;

    const tick = async (): Promise<void> => {
      const next = await load();
      if (cancelled.current) return;
      /**
       * 只在"还在跑"时继续问。`none`（服务端没有任务）也要停：
       * 对着一件不存在的事反复轮询，它不会自己出现 —— 那是老账号
       * （注册早于这套流程），需要客户点一下才建。
       */
      const settled =
        next === null ||
        next.status === 'succeeded' ||
        next.status === 'failed' ||
        next.status === 'none';
      if (settled) return;
      setPolls((n) => n + 1);
      timer = window.setTimeout(() => void tick(), POLL_MS);
    };

    void tick();
    return () => {
      cancelled.current = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [load]);

  const onRetry = async (): Promise<void> => {
    setBusy(true);
    try {
      const next = await retryProvisioning();
      setView(next);
      setError(null);
      setPolls(0);
      // 重试成功后立刻拉一次身份：租户名册变了，控制台要靠它才能进正文
      if (next.status === 'succeeded') onReady();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const status = view?.status ?? 'pending';
  const done = status === 'succeeded';
  const failed = status === 'failed';
  const absent = status === 'none';

  return (
    <section className="provision">
      <h2 className="provision__title">
        {done
          ? '你的站点已就绪'
          : failed
            ? '开通没有跑完'
            : absent
              ? '这个账号还没有站点'
              : '正在为你建站'}
      </h2>
      <p className="provision__sub">
        {done
          ? view?.host
            ? `访问地址：${view.host}。页面还是草稿状态，发布后就能对外访问。`
            : '可以开始编辑了。'
          : failed
            ? '这一步失败了，前面的步骤都保留了，重试会从第一步接着跑（不会重复建租户）。'
            : absent
              ? '建站会创建三个默认页面、一个试用套餐和一个访问地址，随时可以改。'
              : '三个页面、一个试用套餐和一个访问地址，通常一两秒就好。'}
      </p>

      {view && view.steps.length > 0 ? (
        <ol className="provision__steps">
          {view.steps.map((step) => (
            <li key={step.key} className={`provision__step is-${step.status}`}>
              <span className="provision__dot" aria-hidden />
              <span className="provision__label">{step.title}</span>
              {step.status === 'failed' && step.error ? (
                <span className="provision__err">{step.error}</span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : absent ? null : (
        <p className="muted">正在准备第一步…</p>
      )}

      {error ? <p className="error">{error}</p> : null}

      {failed || absent || polls >= MAX_POLLS ? (
        <div className="provision__actions">
          <Button variant="primary" disabled={busy} onClick={() => void onRetry()}>
            {busy ? '处理中…' : failed ? '重试' : absent ? '立即建站' : '重新检查'}
          </Button>
          {view && view.attempts > 0 ? (
            <span className="muted">已尝试 {view.attempts} 次</span>
          ) : null}
        </div>
      ) : done ? (
        <div className="provision__actions">
          <Button variant="primary" onClick={onReady}>
            进入控制台
          </Button>
        </div>
      ) : null}
    </section>
  );
}
